import React, { useState, useEffect } from "react";
import { Trash2, X, Plus } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import SearchableCodeSelect from "../../../common/SearchableCodeSelect";

function blankRule() {
  return {
    filter_type: "include",
    equipment: { from: "", to: "", matching: "", status: "active" },
    service_orders: "active",
    equipment_codes: {
      from_code: "",
      to_code: "",
      revenue: true,
      cost_to_own: true,
      cost_to_operate: true,
    },
  };
}

export default function EquipmentFilterModal({
  open,
  groupUuid,
  companyUuid,
  filter,
  onSave,
  onClose,
  readOnly = false,
}) {
  const [rules, setRules] = useState([blankRule()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const existing = filter?.rules;
    setRules(
      existing && existing.length > 0
        ? existing.map((r) => ({
            filter_type: r.filter_type || "include",
            equipment: {
              from: r.equipment?.from ?? "",
              to: r.equipment?.to ?? "",
              matching: r.equipment?.matching ?? "",
              status: r.equipment?.status ?? "active",
            },
            // legacy saved "open" is now "active"
            service_orders:
              (r.service_orders === "open" ? "active" : r.service_orders) ??
              "active",
            equipment_codes: {
              from_code: r.equipment_codes?.from_code ?? "",
              to_code: r.equipment_codes?.to_code ?? "",
              revenue: r.equipment_codes?.revenue ?? true,
              cost_to_own: r.equipment_codes?.cost_to_own ?? true,
              cost_to_operate: r.equipment_codes?.cost_to_operate ?? true,
            },
          }))
        : [blankRule()]
    );
    setError(null);
  }, [open, filter]);

  if (!open) return null;

  function updateEquipment(index, field, value) {
    setRules((prev) =>
      prev.map((r, i) =>
        i === index
          ? { ...r, equipment: { ...r.equipment, [field]: value } }
          : r
      )
    );
  }

  function updateEquipmentCode(index, field, value) {
    setRules((prev) =>
      prev.map((r, i) =>
        i === index
          ? {
              ...r,
              equipment_codes: { ...r.equipment_codes, [field]: value },
            }
          : r
      )
    );
  }

  function updateServiceOrders(index, value) {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, service_orders: value } : r))
    );
  }

  function updateFilterType(index, value) {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, filter_type: value } : r))
    );
  }

  function removeRule(index) {
    setRules((prev) =>
      prev.length === 1 ? [blankRule()] : prev.filter((_, i) => i !== index)
    );
  }

  function handleSave() {
    const cleanedRules = rules.map((r) => ({
      filter_type: r.filter_type,
      equipment: {
        from: r.equipment.from || null,
        to: r.equipment.to || null,
        matching: r.equipment.matching || null,
        status: r.equipment.status || null,
      },
      service_orders: r.service_orders || null,
      equipment_codes: {
        from_code: r.equipment_codes.from_code || null,
        to_code: r.equipment_codes.to_code || null,
        revenue: r.equipment_codes.revenue,
        cost_to_own: r.equipment_codes.cost_to_own,
        cost_to_operate: r.equipment_codes.cost_to_operate,
      },
    }));

    onSave({ resource: "equipment", mode: "filtered", rules: cleanedRules });
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div className="jfm-container" onClick={(e) => e.stopPropagation()}>
        <div className="jfm-header">
          <span className="jfm-title">Equipment Filters</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <fieldset className="jfm-fieldset" disabled={readOnly}>
            {rules.map((rule, idx) => (
              <div key={idx} className="eqfm-row">
                <select
                  className="jfm-type-select"
                  value={rule.filter_type}
                  onChange={(e) => updateFilterType(idx, e.target.value)}
                >
                  <option value="include">Include</option>
                  <option value="exclude">Exclude</option>
                </select>

                <div className="eqfm-fields">
                  <div className="eqfm-inputs-row">
                    <div className="efm-field">
                      <label>From Equip</label>
                      <SearchableCodeSelect
                        value={rule.equipment.from}
                        companyUuid={companyUuid}
                        placeholder="(first)"
                        fetchOptions={FieldConfigurationAPI.getEquipments}
                        emptyMessage="No equipment found"
                        disabled={readOnly}
                        onChange={(code) => updateEquipment(idx, "from", code)}
                      />
                    </div>
                    <div className="efm-field">
                      <label>To Equip</label>
                      <SearchableCodeSelect
                        value={rule.equipment.to}
                        companyUuid={companyUuid}
                        placeholder="(last)"
                        fetchOptions={FieldConfigurationAPI.getEquipments}
                        emptyMessage="No equipment found"
                        disabled={readOnly}
                        onChange={(code) => updateEquipment(idx, "to", code)}
                      />
                    </div>
                    <div className="efm-field">
                      <label>Fleet Codes Matching</label>
                      <input
                        value={rule.equipment.matching}
                        onChange={(e) =>
                          updateEquipment(idx, "matching", e.target.value)
                        }
                      />
                    </div>
                    <div className="efm-field efm-field--status">
                      <label>Status</label>
                      <select
                        value={rule.equipment.status || "active"}
                        onChange={(e) =>
                          updateEquipment(idx, "status", e.target.value)
                        }
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="">All</option>
                      </select>
                    </div>
                  </div>

                  <div className="eqfm-inputs-row">
                    <div className="efm-field">
                      <label>From Code</label>
                      <SearchableCodeSelect
                        value={rule.equipment_codes.from_code}
                        companyUuid={companyUuid}
                        placeholder="(first)"
                        fetchOptions={FieldConfigurationAPI.getEquipmentCodes}
                        emptyMessage="No equipment codes found"
                        disabled={readOnly}
                        onChange={(code) =>
                          updateEquipmentCode(idx, "from_code", code)
                        }
                      />
                    </div>
                    <div className="efm-field">
                      <label>To Code</label>
                      <SearchableCodeSelect
                        value={rule.equipment_codes.to_code}
                        companyUuid={companyUuid}
                        placeholder="(last)"
                        fetchOptions={FieldConfigurationAPI.getEquipmentCodes}
                        emptyMessage="No equipment codes found"
                        disabled={readOnly}
                        onChange={(code) =>
                          updateEquipmentCode(idx, "to_code", code)
                        }
                      />
                    </div>
                    <div className="efm-field efm-field--status">
                      <label>Service Orders</label>
                      <select
                        value={rule.service_orders || "active"}
                        onChange={(e) =>
                          updateServiceOrders(idx, e.target.value)
                        }
                      >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="none">None</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="eqfm-checkboxes-row">
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.equipment_codes.revenue}
                        onChange={(e) =>
                          updateEquipmentCode(idx, "revenue", e.target.checked)
                        }
                      />
                      Revenue
                    </label>
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.equipment_codes.cost_to_own}
                        onChange={(e) =>
                          updateEquipmentCode(
                            idx,
                            "cost_to_own",
                            e.target.checked
                          )
                        }
                      />
                      Cost to Own
                    </label>
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.equipment_codes.cost_to_operate}
                        onChange={(e) =>
                          updateEquipmentCode(
                            idx,
                            "cost_to_operate",
                            e.target.checked
                          )
                        }
                      />
                      Cost to Operate
                    </label>
                  </div>
                </div>

                {!readOnly && (
                  <button
                    className="jfm-delete"
                    onClick={() => removeRule(idx)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </fieldset>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          {readOnly ? (
            <div />
          ) : (
            <button
              className="jfm-add-row"
              onClick={() => setRules((prev) => [...prev, blankRule()])}
            >
              <Plus size={14} /> Add Row
            </button>
          )}
          <div className="jfm-footer-actions">
            {readOnly ? (
              <button className="jfm-cancel" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="jfm-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="jfm-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
